import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';

import editIcon from 'core/assets/svg/ic-editable-16px.svg';
import { useStore } from 'core/store';
import { HeaderValue } from '../header-value';
import { QuoteSourceModal } from './quote-source-modal';
import useAffinityDisplayName from './use-affinity-display-name';
import useStyles from './quote-source.styles';

// Lead source and affinity code. On Modify Quote it reads the form, so a change made in the Quote
// source modal shows here before the quote is updated. Users who can edit them open the modal.
const QuoteSource = ({ canEdit, className, condensed = false }) => {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const { offer: store } = useStore();
  const formValues = useFormikContext()?.values;

  const leadSource = formValues ? formValues.leadSource : store.offer?.quote?.leadSource;
  const affinityCode = formValues ? formValues.global?.affinity : store.offer?.quote?.global?.affinity;
  const affinityName = useAffinityDisplayName(affinityCode);

  const value = condensed ? (
    <span className={condensed ? classes.wrappedText : classes.text}>
      {affinityName || 'No agency or partner code'}
    </span>
  ) : (
    <span
      className={condensed ? classes.wrappedText : classes.text}
      title={[leadSource, affinityName].filter(Boolean).join(' • ')}
    >
      {leadSource || <span className={classes.empty}>No lead source</span>}
      <span className={classes.separator}>•</span>
      {affinityName || <span className={classes.empty}>No affinity code</span>}
    </span>
  );

  if (!canEdit) {
    return (
      <HeaderValue label={condensed ? 'Agency or partner' : 'Quote source'} className={className}>
        {value}
      </HeaderValue>
    );
  }

  return (
    <HeaderValue label={condensed ? 'Agency or partner' : 'Quote source'} className={className}>
      <button type="button" className={classes.editButton} onClick={() => setModalOpen(true)}>
        {value}
        <img src={editIcon} alt="Edit quote source" className={classes.icon} />
      </button>
      <QuoteSourceModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </HeaderValue>
  );
};

QuoteSource.propTypes = {
  condensed: PropTypes.bool,
  canEdit: PropTypes.bool.isRequired,
  className: PropTypes.string
};

QuoteSource.defaultProps = {
  className: undefined
};

export default observer(QuoteSource);
